import { useEffect, useState } from "react";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import {
  activeImpersonations,
  endImpersonation,
  type ImpersonationSession,
} from "../features/ops/opsApi";
import {
  activeImpersonationForVenue,
  setActiveImpersonation,
  subscribeImpersonation,
} from "../features/ops/impersonationStore";
import { useVenue } from "./VenueContext";

/**
 * The staff-visible impersonation banner (super-admin.md R4, DOMAIN.md §C.13). Non-dismissible, one per
 * active session, naming the operator — so tenant staff always see when a platform operator is inside
 * their venue. It reads the venue's ACTIVE sessions (bootstrap covers reconnect gaps; the SSE
 * `operator.impersonation_*` events refresh it live) AND the operator's own local session (so the
 * operator who started it sees it immediately + can end it). Rendered inside [VenueShellLayout].
 */
export function ImpersonationBanner() {
  const { t } = useTranslation();
  const { venueId } = useVenue();
  const [sessions, setSessions] = useState<ImpersonationSession[]>([]);
  const [local, setLocal] = useState(() => activeImpersonationForVenue(venueId));

  useEffect(
    () => subscribeImpersonation(() => setLocal(activeImpersonationForVenue(venueId))),
    [venueId],
  );

  useEffect(() => {
    let active = true;
    activeImpersonations(venueId)
      .then((s) => active && setSessions(s))
      .catch(() => active && setSessions([]));
    return () => {
      active = false;
    };
  }, [venueId]);

  const ownSessionActive = local !== null && sessions.some((s) => s.id === local.sessionId);
  if (sessions.length === 0 && !local) return null;

  async function stop(sessionId: string) {
    await endImpersonation(sessionId).catch(() => undefined);
    setActiveImpersonation(null);
    setSessions((prev) => prev.filter((s) => s.id !== sessionId));
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className={css({ display: "flex", flexDirection: "column" })}
    >
      {sessions.map((s) => (
        <div
          key={s.id}
          className={css({
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "3",
            px: "4",
            py: "2",
            bg: "amber.9",
            color: "white",
            fontSize: "sm",
            fontWeight: "medium",
          })}
        >
          <span>{t("ops.banner.message", { operator: s.operatorId.slice(0, 8) })}</span>
          {local?.sessionId === s.id && (
            <button
              type="button"
              onClick={() => stop(s.id)}
              className={css({ textDecoration: "underline", cursor: "pointer" })}
            >
              {t("ops.banner.end")}
            </button>
          )}
        </div>
      ))}
      {local && !ownSessionActive && (
        <div className={css({ px: "4", py: "2", bg: "amber.9", color: "white", fontSize: "sm" })}>
          {t("ops.banner.message", { operator: t("ops.banner.you") })}
        </div>
      )}
    </div>
  );
}
